/**
 * Website Channel Configuration Component
 * =========================================
 * Handles creation of website channels with widget configuration and live preview
 *
 * Created: 2025-11-19 - Phase 4 Enhancement
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  Chat,
  CheckCircle,
  ArrowBack,
  ArrowForward,
} from '@mui/icons-material';
import api, { inboxesAPI } from '../services/api';
import { WidgetPreview } from './WidgetPreview';

interface WebsiteChannelConfigProps {
  onClose: () => void;
  onChannelCreated: (channel: any) => void;
}

interface InboxData {
  name: string;
  description: string;
  greeting_enabled: boolean;
  greeting_message: string;
  working_hours_enabled: boolean;
  out_of_office_message: string;
  auto_assignment_enabled: boolean;
  lock_to_single_conversation: boolean;
}

interface WidgetConfigData {
  widget_color: string;
  position: 'left' | 'right';
  widget_style: 'standard' | 'expanded' | 'compact';
  launcher_title: string;
  welcome_heading: string;
  welcome_tagline: string;
  reply_time: string;
  dark_mode: 'light' | 'dark' | 'auto';
  pre_chat_form_enabled: boolean;
  pre_chat_form_message: string;
  business_hours_enabled: boolean;
  email_collect_enabled: boolean;
  csat_enabled: boolean;
  auto_greeting_enabled: boolean;
  auto_greeting_message: string;
  hmac_enabled: boolean;
  locale: string;
}

const steps = ['Basic Details', 'Widget Configuration', 'Review & Create'];

export const WebsiteChannelConfig: React.FC<WebsiteChannelConfigProps> = ({
  onClose,
  onChannelCreated,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [inboxData, setInboxData] = useState<InboxData>({
    name: '',
    description: '',
    greeting_enabled: true,
    greeting_message: 'Hello! How can we help you today?',
    working_hours_enabled: false,
    out_of_office_message: 'We are currently offline. Please leave a message.',
    auto_assignment_enabled: true,
    lock_to_single_conversation: false,
  });

  const [widgetConfig, setWidgetConfig] = useState<WidgetConfigData>({
    widget_color: '#1f93ff',
    position: 'right',
    widget_style: 'standard',
    launcher_title: 'Chat with us',
    welcome_heading: 'Hi there! 👋',
    welcome_tagline: 'How can we help you today?',
    reply_time: 'Typically replies in a few hours',
    dark_mode: 'light',
    pre_chat_form_enabled: false,
    pre_chat_form_message: 'Please provide your details to start the conversation.',
    business_hours_enabled: false,
    email_collect_enabled: false,
    csat_enabled: true,
    auto_greeting_enabled: false,
    auto_greeting_message: 'Hi! Thanks for reaching out. How can I assist you?',
    hmac_enabled: false,  // Disable HMAC for easier testing
    locale: 'en',
  });

  const handleNext = () => {
    if (activeStep === 0 && !inboxData.name) {
      setError('Please enter an inbox name');
      return;
    }
    setError('');
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCreateChannel = async () => {
    setLoading(true);
    setError('');

    try {
      // Step 1: Create the inbox
      const inboxResponse = await inboxesAPI.createInbox({
        name: inboxData.name,
        channel_type: 'website',
        description: inboxData.description,
        greeting_enabled: inboxData.greeting_enabled,
        greeting_message: inboxData.greeting_message,
        working_hours_enabled: inboxData.working_hours_enabled,
        out_of_office_message: inboxData.out_of_office_message,
        auto_assignment_enabled: inboxData.auto_assignment_enabled,
        lock_to_single_conversation: inboxData.lock_to_single_conversation,
        settings: {},
      });

      // Step 2: Create/Update widget configuration
      try {
        // First GET to auto-create default config, then PUT to update with user's settings
        await api.get(`/inboxes/${inboxResponse.id}/widget-config`);
        await api.put(`/inboxes/${inboxResponse.id}/widget-config`, widgetConfig);
      } catch (error) {
        console.error('Failed to create widget config:', error);
        // Continue anyway, widget config can be set later
      }

      // Success!
      onChannelCreated(inboxResponse);
      onClose();
    } catch (error: any) {
      console.error('Failed to create website channel:', error);
      setError(error.response?.data?.detail || 'Failed to create website channel');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Inbox Name"
                  value={inboxData.name}
                  onChange={(e) => setInboxData({ ...inboxData, name: e.target.value })}
                  fullWidth
                  required
                  placeholder="e.g., Website Support"
                  helperText="This name will be visible to your team members"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={inboxData.description}
                  onChange={(e) => setInboxData({ ...inboxData, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Describe the purpose of this inbox"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Basic Settings
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={inboxData.greeting_enabled}
                      onChange={(e) =>
                        setInboxData({ ...inboxData, greeting_enabled: e.target.checked })
                      }
                    />
                  }
                  label="Enable Greeting Message"
                />
                {inboxData.greeting_enabled && (
                  <TextField
                    label="Greeting Message"
                    value={inboxData.greeting_message}
                    onChange={(e) =>
                      setInboxData({ ...inboxData, greeting_message: e.target.value })
                    }
                    fullWidth
                    multiline
                    rows={2}
                    sx={{ mt: 2 }}
                  />
                )}
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={inboxData.auto_assignment_enabled}
                      onChange={(e) =>
                        setInboxData({ ...inboxData, auto_assignment_enabled: e.target.checked })
                      }
                    />
                  }
                  label="Enable Auto Assignment"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={inboxData.lock_to_single_conversation}
                      onChange={(e) =>
                        setInboxData({
                          ...inboxData,
                          lock_to_single_conversation: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Lock to Single Conversation"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Widget Appearance
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                    <TextField
                      label="Widget Color"
                      type="color"
                      value={widgetConfig.widget_color}
                      onChange={(e) =>
                        setWidgetConfig({ ...widgetConfig, widget_color: e.target.value })
                      }
                      fullWidth
                    />

                    <FormControl fullWidth>
                      <InputLabel>Position</InputLabel>
                      <Select
                        value={widgetConfig.position}
                        onChange={(e) =>
                          setWidgetConfig({
                            ...widgetConfig,
                            position: e.target.value as 'left' | 'right',
                          })
                        }
                        label="Position"
                      >
                        <MenuItem value="right">Bottom Right</MenuItem>
                        <MenuItem value="left">Bottom Left</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel>Widget Style</InputLabel>
                      <Select
                        value={widgetConfig.widget_style}
                        onChange={(e) =>
                          setWidgetConfig({
                            ...widgetConfig,
                            widget_style: e.target.value as any,
                          })
                        }
                        label="Widget Style"
                      >
                        <MenuItem value="standard">Standard</MenuItem>
                        <MenuItem value="expanded">Expanded</MenuItem>
                        <MenuItem value="compact">Compact</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel>Dark Mode</InputLabel>
                      <Select
                        value={widgetConfig.dark_mode}
                        onChange={(e) =>
                          setWidgetConfig({
                            ...widgetConfig,
                            dark_mode: e.target.value as any,
                          })
                        }
                        label="Dark Mode"
                      >
                        <MenuItem value="light">Light</MenuItem>
                        <MenuItem value="dark">Dark</MenuItem>
                        <MenuItem value="auto">Auto (System)</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      label="Launcher Title"
                      value={widgetConfig.launcher_title}
                      onChange={(e) =>
                        setWidgetConfig({ ...widgetConfig, launcher_title: e.target.value })
                      }
                      fullWidth
                    />

                    <TextField
                      label="Welcome Heading"
                      value={widgetConfig.welcome_heading}
                      onChange={(e) =>
                        setWidgetConfig({ ...widgetConfig, welcome_heading: e.target.value })
                      }
                      fullWidth
                    />

                    <TextField
                      label="Welcome Tagline"
                      value={widgetConfig.welcome_tagline}
                      onChange={(e) =>
                        setWidgetConfig({ ...widgetConfig, welcome_tagline: e.target.value })
                      }
                      fullWidth
                    />

                    <TextField
                      label="Reply Time"
                      value={widgetConfig.reply_time}
                      onChange={(e) =>
                        setWidgetConfig({ ...widgetConfig, reply_time: e.target.value })
                      }
                      fullWidth
                    />

                    <Divider />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={widgetConfig.csat_enabled}
                          onChange={(e) =>
                            setWidgetConfig({ ...widgetConfig, csat_enabled: e.target.checked })
                          }
                        />
                      }
                      label="Enable CSAT Survey"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={widgetConfig.pre_chat_form_enabled}
                          onChange={(e) =>
                            setWidgetConfig({
                              ...widgetConfig,
                              pre_chat_form_enabled: e.target.checked,
                            })
                          }
                        />
                      }
                      label="Enable Pre-Chat Form"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={widgetConfig.auto_greeting_enabled}
                          onChange={(e) =>
                            setWidgetConfig({
                              ...widgetConfig,
                              auto_greeting_enabled: e.target.checked,
                            })
                          }
                        />
                      }
                      label="Enable Auto Greeting"
                    />

                    {widgetConfig.auto_greeting_enabled && (
                      <TextField
                        label="Auto Greeting Message"
                        value={widgetConfig.auto_greeting_message}
                        onChange={(e) =>
                          setWidgetConfig({
                            ...widgetConfig,
                            auto_greeting_message: e.target.value,
                          })
                        }
                        fullWidth
                        multiline
                        rows={2}
                      />
                    )}
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <WidgetPreview config={widgetConfig} />
              </Grid>
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Review Your Configuration
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Inbox Details
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2">
                        <strong>Name:</strong> {inboxData.name}
                      </Typography>
                      {inboxData.description && (
                        <Typography variant="body2">
                          <strong>Description:</strong> {inboxData.description}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        <strong>Auto Assignment:</strong>{' '}
                        {inboxData.auto_assignment_enabled ? 'Enabled' : 'Disabled'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Greeting:</strong>{' '}
                        {inboxData.greeting_enabled ? 'Enabled' : 'Disabled'}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Widget Configuration
                    </Typography>
                    <Box>
                      <Typography variant="body2">
                        <strong>Position:</strong>{' '}
                        {widgetConfig.position === 'right' ? 'Bottom Right' : 'Bottom Left'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Style:</strong> {widgetConfig.widget_style}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Dark Mode:</strong> {widgetConfig.dark_mode}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Launcher Title:</strong> {widgetConfig.launcher_title}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Welcome Heading:</strong> {widgetConfig.welcome_heading}
                      </Typography>
                      <Typography variant="body2">
                        <strong>CSAT Survey:</strong>{' '}
                        {widgetConfig.csat_enabled ? 'Enabled' : 'Disabled'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Pre-Chat Form:</strong>{' '}
                        {widgetConfig.pre_chat_form_enabled ? 'Enabled' : 'Disabled'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <WidgetPreview config={widgetConfig} />
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh',
        overflow: 'hidden',
        p: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexShrink: 0 }}>
        <Chat sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Create Website Widget Channel
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Set up your website widget with live preview
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {renderStepContent(index)}

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button disabled={activeStep === 0} onClick={handleBack} startIcon={<ArrowBack />}>
                    Back
                  </Button>
                  <Box sx={{ flex: '1 1 auto' }} />
                  {activeStep === steps.length - 1 ? (
                    <Button
                      variant="contained"
                      onClick={handleCreateChannel}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                    >
                      {loading ? 'Creating...' : 'Create Channel'}
                    </Button>
                  ) : (
                    <Button variant="contained" onClick={handleNext} endIcon={<ArrowForward />}>
                      Next
                    </Button>
                  )}
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>
    </Box>
  );
};

export default WebsiteChannelConfig;
